import { useSyncExternalStore } from 'react';
import {
  BACK_SHORTCUT,
  FORWARD_SHORTCUT,
  NEW_TASK_SHORTCUT,
  SETTINGS_SHORTCUT,
  SHORTCUTS_SHORTCUT,
  SIDEBAR_SIDE_SHORTCUT,
  TOGGLE_SIDEBAR_SHORTCUT,
} from '@/components/sidebar/shell-shortcuts';
import { PERMISSIONS_SHORTCUT } from '@/lib/desktop-shell/composer-permissions';
import { SIDEBAR_SEARCH_SHORTCUT } from '@/lib/desktop-shell/sidebar-search';
import { readDevicePreference, writeDevicePreference } from '@/lib/device-preferences';
import { isMacPlatform, type KeyboardShortcut, shortcutLabel } from '@/lib/platform';
import { SIDE_PANEL_ITEMS } from '@/lib/side-panel/side-panel-items';

/*
 * Settings › Keyboard shortcuts (D19, desktop only): the app's own shortcuts, their default keys
 * and the keys the user chose instead, kept on this device. Every handler of one of these reads
 * its keys with `currentShortcut`, so a change applies at once.
 */

export type ShortcutCommand = {
  id: string;
  title: string;
  group: 'everywhere' | 'conversation';
  shortcut: KeyboardShortcut;
  /** Only the Desktop app has it (a system-wide shortcut). */
  desktopOnly?: boolean;
};

export const SHORTCUT_GROUPS = [
  { id: 'everywhere', title: 'Everywhere' },
  { id: 'conversation', title: 'In a conversation' },
] as const;

/** ⇧⌘P: search and commands, on the commands. */
export const COMMAND_PALETTE_SHORTCUT: KeyboardShortcut = {
  key: 'p',
  commandOrControl: true,
  shift: true,
};
/** ⌥Space: dictation into Crowdex, from any app (the Desktop's system-wide shortcut). */
const DICTATE_SHORTCUT: KeyboardShortcut = { key: 'Space', alt: true };
/** ⇧⌘A in the composer: its artifacts picker. */
export const ATTACH_ARTIFACT_SHORTCUT: KeyboardShortcut = {
  key: 'a',
  commandOrControl: true,
  shift: true,
};

/** ⌘J: shows or hides the side panel. */
export const TOGGLE_PANEL_SHORTCUT: KeyboardShortcut = { key: 'j', commandOrControl: true };
/** The conversation's ⋮ menu (D6): its side chat, find, rename and archive. */
export const SIDE_CHAT_SHORTCUT: KeyboardShortcut = {
  key: 'l',
  commandOrControl: true,
  shift: true,
};
export const FIND_SHORTCUT: KeyboardShortcut = { key: 'f', commandOrControl: true };
export const RENAME_SHORTCUT: KeyboardShortcut = { key: 'F2' };
export const ARCHIVE_SHORTCUT: KeyboardShortcut = { key: 'Backspace', commandOrControl: true };
/** ⌥⌘→: in a split, the next conversation becomes the one in use (its composer takes the keys). */
export const NEXT_PANE_SHORTCUT: KeyboardShortcut = {
  key: 'ArrowRight',
  commandOrControl: true,
  alt: true,
};

/** The id of the shortcut opening a panel tool (`SIDE_PANEL_ITEMS`). */
export const panelShortcutId = (view: string) => `panel:${view}`;

let commands: readonly ShortcutCommand[] | null = null;

/**
 * The commands and their default keys. Built on first use: the shell's own definitions import
 * this module back.
 */
export function shortcutCommands(): readonly ShortcutCommand[] {
  if (commands !== null) return commands;
  commands = [
    { id: 'new-task', title: 'New conversation', group: 'everywhere', shortcut: NEW_TASK_SHORTCUT },
    {
      id: 'search',
      title: 'Search and commands',
      group: 'everywhere',
      shortcut: SIDEBAR_SEARCH_SHORTCUT,
    },
    {
      id: 'command-palette',
      title: 'Commands',
      group: 'everywhere',
      shortcut: COMMAND_PALETTE_SHORTCUT,
    },
    {
      id: 'toggle-sidebar',
      title: 'Toggle sidebar',
      group: 'everywhere',
      shortcut: TOGGLE_SIDEBAR_SHORTCUT,
    },
    {
      id: 'sidebar-side',
      title: 'Switch the sidebar between conversations and the tool',
      group: 'everywhere',
      shortcut: SIDEBAR_SIDE_SHORTCUT,
    },
    { id: 'back', title: 'Back', group: 'everywhere', shortcut: BACK_SHORTCUT },
    { id: 'forward', title: 'Forward', group: 'everywhere', shortcut: FORWARD_SHORTCUT },
    { id: 'settings', title: 'Settings', group: 'everywhere', shortcut: SETTINGS_SHORTCUT },
    {
      id: 'keyboard-shortcuts',
      title: 'Keyboard shortcuts',
      group: 'everywhere',
      shortcut: SHORTCUTS_SHORTCUT,
    },
    {
      id: 'dictate',
      title: 'Dictate (from any app)',
      group: 'everywhere',
      shortcut: DICTATE_SHORTCUT,
      desktopOnly: true,
    },
    {
      id: 'toggle-panel',
      title: 'Show or hide the panel',
      group: 'conversation',
      shortcut: TOGGLE_PANEL_SHORTCUT,
    },
    { id: 'side-chat', title: 'Side chat', group: 'conversation', shortcut: SIDE_CHAT_SHORTCUT },
    {
      id: 'find',
      title: 'Find in the conversation',
      group: 'conversation',
      shortcut: FIND_SHORTCUT,
    },
    {
      id: 'next-pane',
      title: 'Go to the next conversation of the split',
      group: 'conversation',
      shortcut: NEXT_PANE_SHORTCUT,
    },
    {
      id: 'rename',
      title: 'Rename the conversation',
      group: 'conversation',
      shortcut: RENAME_SHORTCUT,
    },
    {
      id: 'archive',
      title: 'Archive the conversation',
      group: 'conversation',
      shortcut: ARCHIVE_SHORTCUT,
    },
    ...SIDE_PANEL_ITEMS.flatMap((item) =>
      'shortcut' in item && item.shortcut !== undefined
        ? [
            {
              id: panelShortcutId(item.id),
              title: `Open ${item.label}`,
              group: 'conversation' as const,
              shortcut: item.shortcut,
            },
          ]
        : [],
    ),
    {
      id: 'attach-artifact',
      title: 'Add an artifact to the message',
      group: 'conversation',
      shortcut: ATTACH_ARTIFACT_SHORTCUT,
    },
    {
      id: 'permissions',
      title: 'Next permissions',
      group: 'conversation',
      shortcut: PERMISSIONS_SHORTCUT,
    },
  ];
  return commands;
}

export const SHORTCUTS_KEY = 'crowdex.shortcuts';
export const SHORTCUTS_CHANGED_EVENT = 'crowdex-shortcuts-changed';

type Overrides = Record<string, KeyboardShortcut>;

function isShortcut(value: unknown): value is KeyboardShortcut {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { key?: unknown }).key === 'string' &&
    (value as { key: string }).key.length > 0
  );
}

let cached: Overrides | null = null;

/** Read again on the next use: the keys changed here or in another window. */
function forgetOverrides(): void {
  cached = null;
}
if (typeof window !== 'undefined') {
  window.addEventListener(SHORTCUTS_CHANGED_EVENT, forgetOverrides);
  window.addEventListener('storage', forgetOverrides);
}

/** The keys the user chose, parsed once until they change; keys no command may have are dropped. */
export function readShortcutOverrides(): Overrides {
  if (cached !== null) return cached;
  let overrides: Overrides = {};
  try {
    const parsed = JSON.parse(readDevicePreference(SHORTCUTS_KEY) ?? '{}') as unknown;
    if (typeof parsed === 'object' && parsed !== null)
      overrides = Object.fromEntries(
        Object.entries(parsed).filter(
          ([id, value]) => isShortcut(value) && shortcutProblem(id, value) === null,
        ),
      );
  } catch {
    overrides = {};
  }
  cached = overrides;
  return overrides;
}

/** The keys of a command: the user's, else its default. */
export function currentShortcut(
  id: string,
  overrides = readShortcutOverrides(),
): KeyboardShortcut | undefined {
  return overrides[id] ?? shortcutCommands().find((command) => command.id === id)?.shortcut;
}

/** Sets a command's keys; its default keys remove the override. */
export async function writeShortcut(id: string, shortcut: KeyboardShortcut | null) {
  const overrides = { ...readShortcutOverrides() };
  const fallback = shortcutCommands().find((command) => command.id === id)?.shortcut;
  if (shortcut === null || (fallback && sameShortcut(shortcut, fallback))) delete overrides[id];
  else overrides[id] = shortcut;
  await writeDevicePreference(SHORTCUTS_KEY, JSON.stringify(overrides));
  forgetOverrides();
  window.dispatchEvent(new Event(SHORTCUTS_CHANGED_EVENT));
}

function sameShortcut(left: KeyboardShortcut, right: KeyboardShortcut): boolean {
  return (
    left.key.toLowerCase() === right.key.toLowerCase() &&
    Boolean(left.commandOrControl) === Boolean(right.commandOrControl) &&
    Boolean(left.shift) === Boolean(right.shift) &&
    Boolean(left.alt) === Boolean(right.alt)
  );
}

const MODIFIER_KEYS = new Set(['Meta', 'Control', 'Shift', 'Alt', 'OS', 'Hyper', 'Super']);

/** The shortcut a key press makes while recording, or `null` for a lone modifier. */
export function shortcutFromEvent(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'code'>,
  mac = isMacPlatform(),
): KeyboardShortcut | null {
  if (MODIFIER_KEYS.has(event.key)) return null;
  // ⌥ changes the character a letter types on macOS: the physical key names it.
  const key =
    event.key === ' '
      ? 'Space'
      : event.altKey && /^Key[A-Z]$/.test(event.code)
        ? event.code.slice(3).toLowerCase()
        : event.key.length === 1
          ? event.key.toLowerCase()
          : event.key;
  const shortcut: KeyboardShortcut = { key };
  if (mac ? event.metaKey : event.ctrlKey) shortcut.commandOrControl = true;
  if (event.shiftKey) shortcut.shift = true;
  if (event.altKey) shortcut.alt = true;
  return shortcut;
}

const FUNCTION_KEY = /^F(?:[1-9]|1[0-9]|2[0-4])$/;
/** Commands whose keys may not be Esc alone: the system-wide one, and the composer's own. */
const NO_BARE_ESCAPE = new Set(['dictate', 'attach-artifact']);

/**
 * Why keys cannot be a command's shortcut, or `null` when they can. A key alone would fire
 * while typing (in a field, or in every app for Dictate): it needs ⌘/Ctrl or ⌥/Alt, except
 * F1–F24, and Esc (Deny, Close) for the window's own commands.
 */
export function shortcutProblem(
  id: string,
  shortcut: KeyboardShortcut,
  mac = isMacPlatform(),
): string | null {
  if (shortcut.commandOrControl || shortcut.alt) return null;
  if (FUNCTION_KEY.test(shortcut.key)) return null;
  if (shortcut.key === 'Escape' && !NO_BARE_ESCAPE.has(id)) return null;
  return `Add ${mac ? '⌘ or ⌥' : 'Ctrl or Alt'}: a key alone types`;
}

/**
 * What a key press while recording gives a command: its keys, why they can't be its keys, or
 * `null` for a lone modifier (its key is still to come). ⌃ on a Mac is refused: Crowdex's
 * shortcuts have ⌘ there, and ⌃ would otherwise be recorded as the bare key.
 */
export function recordShortcut(
  id: string,
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'code'>,
  mac = isMacPlatform(),
): { shortcut: KeyboardShortcut } | { problem: string } | null {
  const shortcut = shortcutFromEvent(event, mac);
  if (shortcut === null) return null;
  if (mac && event.ctrlKey) return { problem: '⌃ can’t be used: use ⌘ or ⌥' };
  if (!mac && event.metaKey) return { problem: 'The Windows key can’t be used: use Ctrl or Alt' };
  const problem = shortcutProblem(id, shortcut, mac);
  return problem === null ? { shortcut } : { problem };
}

/**
 * Whether a keydown may run a shortcut where it happened: in a field, only with ⌘/Ctrl. The
 * keys a field types (a letter, ⌥ letters on a Mac, Esc, an F-key it may use) stay the field's.
 */
export function shortcutAllowedFrom(
  event: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'target'>,
  mac = isMacPlatform(),
): boolean {
  if (mac ? event.metaKey : event.ctrlKey) return true;
  const target = event.target;
  if (!(target instanceof HTMLElement)) return true;
  return !(
    target.isContentEditable ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLInputElement &&
      !['button', 'checkbox', 'radio', 'range', 'reset', 'submit'].includes(target.type))
  );
}

/** Another command already using these keys, if any. */
export function conflictingCommand(
  id: string,
  shortcut: KeyboardShortcut,
  overrides = readShortcutOverrides(),
): ShortcutCommand | null {
  return (
    shortcutCommands().find((command) => {
      if (command.id === id) return false;
      const keys = currentShortcut(command.id, overrides);
      return keys !== undefined && sameShortcut(keys, shortcut);
    }) ?? null
  );
}

const MAC_GLYPHS: Readonly<Record<string, string>> = {
  Enter: '↵',
  Escape: 'Esc',
  Tab: 'Tab',
  Space: 'Space',
  Backspace: '⌫',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

/** The keycaps of a shortcut, in the order the board draws them (`⌘` `⇧` `L`). */
export function shortcutKeycaps(shortcut: KeyboardShortcut, mac = isMacPlatform()): string[] {
  const key =
    MAC_GLYPHS[shortcut.key] ??
    (shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key);
  if (mac)
    return [
      ...(shortcut.commandOrControl ? ['⌘'] : []),
      ...(shortcut.alt ? ['⌥'] : []),
      ...(shortcut.shift ? ['⇧'] : []),
      key === '↵' ? '↵' : key,
    ];
  return [
    ...(shortcut.commandOrControl ? ['Ctrl'] : []),
    ...(shortcut.alt ? ['Alt'] : []),
    ...(shortcut.shift ? ['Shift'] : []),
    key === '↵' ? 'Enter' : key,
  ];
}

/** An Electron accelerator for a shortcut (`Alt+Space`, `CommandOrControl+Shift+L`). */
export function electronAccelerator(shortcut: KeyboardShortcut): string {
  const named: Readonly<Record<string, string>> = {
    ' ': 'Space',
    Escape: 'Esc',
    ArrowUp: 'Up',
    ArrowDown: 'Down',
    ArrowLeft: 'Left',
    ArrowRight: 'Right',
  };
  const key =
    named[shortcut.key] ?? (shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key);
  return [
    shortcut.commandOrControl && 'CommandOrControl',
    shortcut.alt && 'Alt',
    shortcut.shift && 'Shift',
    key,
  ]
    .filter(Boolean)
    .join('+');
}

function subscribeShortcuts(onChange: () => void): () => void {
  window.addEventListener(SHORTCUTS_CHANGED_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(SHORTCUTS_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** The user's keys, rendering again when they change: for labels that show them. */
export function useShortcutOverrides(): Readonly<Record<string, KeyboardShortcut>> {
  return useSyncExternalStore(subscribeShortcuts, readShortcutOverrides, () => EMPTY_OVERRIDES);
}
const EMPTY_OVERRIDES: Readonly<Record<string, KeyboardShortcut>> = {};

/** A command's keys as menus write them: the user's, else its own. */
export function commandLabel(id: string, fallback: KeyboardShortcut): string {
  return shortcutLabel(currentShortcut(id) ?? fallback);
}
